var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { storeToRefs } from "pinia";
import { BModal, BFormGroup, BFormInput, BListGroup, BListGroupItem, BBadge, BButtonGroup, BButton, } from "bootstrap-vue";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
import UtcDate from "@/components/UtcDate.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import localize from "@/utils/localization";
import Heading from "@/components/Common/Heading.vue";
import { useRouter } from "vue-router/composables";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import Filtering, { contains, expandNameTag } from "@/utils/filtering";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faColumns, faSignInAlt, faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { faListAlt } from "@fortawesome/free-regular-svg-icons";
import { useInfiniteScroll } from "@vueuse/core";
import isEqual from "lodash.isequal";
export default /*#__PURE__*/ _defineComponent({
    __name: 'SelectorModal',
    props: {
        multiple: { type: Boolean, default: false },
        title: { type: String, default: "Switch to history" },
        histories: { type: Array, default: () => [] },
        additionalOptions: { type: Array, default: () => [] },
        showModal: { type: Boolean, default: false },
    },
    emits: ["selectHistory", "selectHistories", "update:show-modal"],
    setup(__props, { emit }) {
        const props = __props;
        const validFilters = {
            name: contains("name"),
            tag: contains("tags", "tag", expandNameTag),
            annotation: contains("annotation"),
        };
        const HistoriesFilters = new Filtering(validFilters, false);
        library.add(faColumns, faSignInAlt, faListAlt, faArrowDown);
        const propShowModal = computed({
            get: () => {
                return props.showModal;
            },
            set: (val) => {
                emit("update:show-modal", val);
            },
        });
        const selectedHistories = ref([]);
        const filter = ref("");
        const busy = ref(false);
        const modal = ref(null);
        const scrollableDiv = ref(null);
        const historyStore = useHistoryStore();
        const { currentHistoryId, totalHistoryCount } = storeToRefs(useHistoryStore());
        const { currentUser } = storeToRefs(useUserStore());
        const pinnedHistories = computed(() => historyStore.pinnedHistories);
        const hasNoResults = computed(() => filter.value && filtered.value.length == 0);
        const validFilter = computed(() => filter.value && filter.value.length > 2);
        const allLoaded = computed(() => totalHistoryCount.value <= filtered.value.length);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            useInfiniteScroll(scrollableDiv.value, () => loadMore());
        }));
        // retain previously selected histories when you reopen the modal in multi view
        watch(() => propShowModal.value, (show) => {
            if (props.multiple && show) {
                selectedHistories.value = [...pinnedHistories.value];
            }
        }, {
            immediate: true,
        });
        onUnmounted(() => {
            // Remove the infinite scrolling behavior
            useInfiniteScroll(scrollableDiv.value, () => { });
        });
        watch(() => filter.value, (newVal, oldVal) => __awaiter(this, void 0, void 0, function* () {
            if (newVal !== "" && validFilter.value && newVal !== oldVal) {
                yield loadMore(true);
            }
        }));
        // reactive proxy for props.histories, as the prop is not
        // always guaranteed to be reactive for some strange reason.
        // TODO: Re investigate when upgrading to vue3
        const historiesProxy = ref([]);
        watch(() => props.histories, (h) => {
            historiesProxy.value = h.filter((h) => { var _a, _b; return !h.user_id || (!((_a = currentUser.value) === null || _a === void 0 ? void 0 : _a.isAnonymous) && h.user_id === ((_b = currentUser.value) === null || _b === void 0 ? void 0 : _b.id)); });
        }, {
            immediate: true,
        });
        const filtered = computed(() => {
            let filteredHistories = [];
            if (!validFilter.value) {
                filteredHistories = historiesProxy.value;
            }
            else {
                const filters = HistoriesFilters.getFiltersForText(filter.value);
                filteredHistories = historiesProxy.value.filter((history) => {
                    if (!HistoriesFilters.testFilters(filters, history)) {
                        return false;
                    }
                    return true;
                });
            }
            return filteredHistories.sort((a, b) => {
                if (a.id == currentHistoryId.value) {
                    return -1;
                }
                else if (b.id == currentHistoryId.value) {
                    return 1;
                }
                else if (a.update_time < b.update_time) {
                    return 1;
                }
                else {
                    return -1;
                }
            });
        });
        function historyClicked(history) {
            var _a;
            if (props.multiple) {
                const index = selectedHistories.value.findIndex((item) => item.id == history.id);
                if (index !== -1) {
                    selectedHistories.value.splice(index, 1);
                }
                else {
                    selectedHistories.value.push({ id: history.id });
                }
            }
            else {
                emit("selectHistory", history);
                (_a = modal.value) === null || _a === void 0 ? void 0 : _a.hide();
            }
        }
        function selectHistories() {
            var _a;
            // set value of pinned histories in store
            emit("selectHistories", selectedHistories.value);
            // set local value equal to updated value from store
            selectedHistories.value = pinnedHistories.value;
            (_a = modal.value) === null || _a === void 0 ? void 0 : _a.hide();
        }
        const router = useRouter();
        function setCurrentHistory(history) {
            var _a;
            historyStore.setCurrentHistory(history.id);
            (_a = modal.value) === null || _a === void 0 ? void 0 : _a.hide();
        }
        function setCenterPanelHistory(history) {
            var _a;
            router.push(`/histories/view?id=${history.id}`);
            (_a = modal.value) === null || _a === void 0 ? void 0 : _a.hide();
        }
        function openInMulti(history) {
            var _a;
            router.push("/histories/view_multiple");
            historyStore.pinHistory(history.id);
            historyStore.loadHistoryById(history.id);
            (_a = modal.value) === null || _a === void 0 ? void 0 : _a.hide();
        }
        /** Loads (paginates) for more histories
         * @param noScroll If true, we are not scrolling and will load _all_ items for current filter
         */
        function loadMore(noScroll = false) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!busy.value && (noScroll || (!noScroll && !filter.value && !allLoaded.value))) {
                    busy.value = true;
                    const queryString = filter.value && HistoriesFilters.getQueryString(filter.value);
                    yield historyStore.loadHistories(true, queryString);
                    busy.value = false;
                }
            });
        }
        return { __sfc: true, validFilters, HistoriesFilters, props, emit, propShowModal, selectedHistories, filter, busy, modal, scrollableDiv, historyStore, currentHistoryId, totalHistoryCount, currentUser, pinnedHistories, hasNoResults, validFilter, allLoaded, historiesProxy, filtered, historyClicked, selectHistories, router, setCurrentHistory, setCenterPanelHistory, openInMulti, loadMore, BModal, BFormGroup, BFormInput, BListGroup, BListGroupItem, BBadge, BButtonGroup, BButton, StatelessTags, UtcDate, localize, Heading, FontAwesomeIcon, isEqual };
    }
});
